import React, { useState } from "react";
import { Box, Drawer, IconButton, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { useHistory, useLocation } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DashboardIcon from "@mui/icons-material/Dashboard";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SettingsIcon from "@mui/icons-material/Settings";
import VaccinesIcon from "@mui/icons-material/Vaccines";

import { IPD_COLORS, IPD_SHAPE } from "../../ipd/utils";
import { IPD_SIDEBAR } from "../../ipd/utils/ipdSidebar";
import { NAV } from "../constants";

/**
 * The Operation Theatre module shell — a dark collapsible left sidebar that
 * every OT screen renders inside, so the module reads as one contained app
 * rather than a set of unrelated pages under the hospital's top navbar.
 *
 * Deliberately the same shape as `EmergencyShell`: a fixed-width pane on
 * desktop that collapses to an icon rail, becoming an off-canvas drawer below
 * ~1200px. The chrome uses the shared IPD_SIDEBAR palette, the same one
 * Emergency and the inventory suite use — a second navigation idiom in this app
 * would make OT feel like a different product.
 *
 * The nav itself comes from `constants.NAV`, so a new screen is one entry there
 * plus a route in App.js.
 */

const ICONS = {
  dashboard: DashboardIcon,
  schedule: CalendarMonthIcon,
  cases: FormatListBulletedIcon,
  board: MonitorHeartIcon,
  final_list: FactCheckIcon,
  reports: AssessmentIcon,
  masters: SettingsIcon,
};

const EXPANDED = 248;
const COLLAPSED = 72;

/** The nav entry the current path belongs to.
 *
 *  Longest-prefix, not first-match: every OT route starts with the dashboard's
 *  own path, so a plain `startsWith` would light "Dashboard" on every screen.
 *  A nested route (a case detail) keeps its parent entry lit.
 */
function activeRoute(pathname) {
  return NAV.reduce((best, item) => {
    if (!pathname.startsWith(item.route)) return best;
    if (!best || item.route.length > best.route.length) return item;
    return best;
  }, null);
}

function SidebarContent({ collapsed, pathname, onNavigate }) {
  const active = activeRoute(pathname);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        background: `linear-gradient(180deg, ${IPD_SIDEBAR.bg} 0%, ${IPD_SIDEBAR.bgGradientEnd} 100%)`,
        color: IPD_SIDEBAR.text,
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: collapsed ? 0 : 2.25,
          py: 2,
          justifyContent: collapsed ? "center" : "flex-start",
          borderBottom: `1px solid ${IPD_SIDEBAR.border}`,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            borderRadius: "9px",
            display: "grid",
            placeItems: "center",
            bgcolor: IPD_COLORS.primaryLight,
            color: "#fff",
          }}
        >
          <VaccinesIcon sx={{ fontSize: 20 }} />
        </Box>
        {!collapsed && (
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{ fontSize: 14, fontWeight: 800, color: "#fff", lineHeight: 1.2, whiteSpace: "nowrap" }}
            >
              Operation Theatre
            </Typography>
            <Typography sx={{ fontSize: 11, color: IPD_SIDEBAR.label, whiteSpace: "nowrap" }}>
              Surgery
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        component="nav"
        sx={{
          flex: 1,
          py: 1.5,
          px: collapsed ? 1 : 1.25,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        {NAV.map((item) => {
          const isActive = active && active.id === item.id;
          const Icon = ICONS[item.id] || FormatListBulletedIcon;
          const button = (
            <Box
              component="button"
              type="button"
              onClick={() => onNavigate(item.route)}
              aria-current={isActive ? "page" : undefined}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                width: "100%",
                justifyContent: collapsed ? "center" : "flex-start",
                px: collapsed ? 0 : 1.5,
                py: 1.1,
                borderRadius: "9px",
                cursor: "pointer",
                border: "none",
                fontFamily: "inherit",
                textAlign: "left",
                color: isActive ? "#fff" : IPD_SIDEBAR.text,
                bgcolor: isActive ? IPD_SIDEBAR.active : "transparent",
                transition: "background .15s ease, color .15s ease",
                "&:hover": {
                  bgcolor: isActive ? IPD_SIDEBAR.active : IPD_SIDEBAR.hover,
                  color: "#fff",
                },
                "&:focus-visible": {
                  outline: `2px solid ${IPD_COLORS.selectedBorder}`,
                  outlineOffset: 2,
                },
              }}
            >
              <Icon sx={{ fontSize: 20, flexShrink: 0 }} />
              {!collapsed && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: 13.5,
                    fontWeight: isActive ? 700 : 500,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {item.label}
                </Typography>
              )}
            </Box>
          );
          return collapsed ? (
            <Tooltip key={item.id} title={item.label} placement="right">
              {button}
            </Tooltip>
          ) : (
            <React.Fragment key={item.id}>{button}</React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

export default function OtShell({ children }) {
  const history = useHistory();
  const location = useLocation();
  // Fixed pane only on large screens; below this it becomes a drawer so the
  // wide grids (the board, the reports) keep the full width on a laptop.
  const isMobile = useMediaQuery("(max-width:1199px)");
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const navigate = (to) => {
    history.push(to);
    setMobileOpen(false);
  };

  const width = collapsed ? COLLAPSED : EXPANDED;

  return (
    <Box sx={{ display: "flex", minHeight: "100%", bgcolor: IPD_COLORS.pageBg }}>
      {!isMobile && (
        <Box
          sx={{
            position: "sticky",
            top: 0,
            alignSelf: "flex-start",
            flexShrink: 0,
            width,
            height: "100dvh",
            transition: "width .2s ease",
          }}
        >
          <SidebarContent collapsed={collapsed} pathname={location.pathname} onNavigate={navigate} />
          <IconButton
            onClick={() => setCollapsed((c) => !c)}
            size="small"
            aria-label={collapsed ? "Expand the menu" : "Collapse the menu"}
            sx={{
              position: "absolute",
              top: 18,
              right: -13,
              zIndex: 2,
              width: 26,
              height: 26,
              bgcolor: IPD_COLORS.cardBg,
              border: `1px solid ${IPD_COLORS.border}`,
              boxShadow: IPD_COLORS.shadow.card,
              color: IPD_COLORS.textSecondary,
              "&:hover": { bgcolor: IPD_COLORS.cardBg, color: IPD_COLORS.primary },
            }}
          >
            {collapsed ? (
              <ChevronRightIcon sx={{ fontSize: 17 }} />
            ) : (
              <ChevronLeftIcon sx={{ fontSize: 17 }} />
            )}
          </IconButton>
        </Box>
      )}

      {isMobile && (
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          PaperProps={{ sx: { width: EXPANDED, border: "none" } }}
        >
          <SidebarContent collapsed={false} pathname={location.pathname} onNavigate={navigate} />
        </Drawer>
      )}

      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        {isMobile && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1,
              borderBottom: `1px solid ${IPD_COLORS.border}`,
              bgcolor: IPD_COLORS.cardBg,
            }}
          >
            <IconButton onClick={() => setMobileOpen(true)} size="small" aria-label="Open the menu">
              <MenuIcon />
            </IconButton>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: IPD_COLORS.textPrimary }}>
              Operation Theatre
            </Typography>
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0, borderRadius: `${IPD_SHAPE.radiusMd}px` }}>{children}</Box>
      </Box>
    </Box>
  );
}
